import { Injectable, Signal, effect, inject } from '@angular/core';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEvent, KeycloakEventType } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { EnvironmentService } from './environment.service';
import { BehaviorSubject } from 'rxjs';
import { IAuthService } from '../interfaces/i-auth.service';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class AuthKeycloakService implements IAuthService {

  isUserLogged = new BehaviorSubject<boolean>(false);
  userIdLoaded$ = new BehaviorSubject<number>(null);
  private readonly USER_INFO_KEY = 'cartoaf-user';
  private cachedUserId: number = null;
  private isLoadingUser = false;
  private readonly keycloak = inject(Keycloak);
  private readonly keycloakEventSignal = inject<Signal<KeycloakEvent>>(KEYCLOAK_EVENT_SIGNAL);
  
  constructor(
    private environmentService: EnvironmentService,
    public apiService: ApiService
  ) {
    effect(() => {
      const event = this.keycloakEventSignal();
      switch(event.type) {
        case KeycloakEventType.AuthSuccess:
          if(environmentService.environment.keycloak.updateUser)
          this.apiService.updateUser().subscribe({error: error => console.error(error)})
          this.getUserInfo();
        break;
        case KeycloakEventType.TokenExpired:
          this.keycloak.updateToken(20).catch(error => {
            console.error(error)
          })
        break;
      }
    });
  }

  getJwtToken() {
    return this.keycloak.token;
  }
  getID() {
    if (this.cachedUserId !== null) {
      return this.cachedUserId;
    }

    const cached = sessionStorage.getItem(this.USER_INFO_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        this.cachedUserId = parsed?.__id ?? null;
        return this.cachedUserId;
      } catch (error) {
        console.error('Errore nella lettura dello user cache', error);
        sessionStorage.removeItem(this.USER_INFO_KEY);
      }
    }

    this.getUserInfo();
    return this.cachedUserId;
  }
  envMessageToString() {
    throw new Error('Method not implemented.');
  }
  resetPassword(userId: string, password: string) {
    throw new Error('Method not implemented.');
  }
  tempPassword(userId: string) {
    throw new Error('Method not implemented.');
  }
  
  login(username: string, password: string) {
    throw new Error('Method not implemented.');
  }

  redirectToLoginPage() {
    this.keycloak.login();
  }

  getUsername() {
    return this.getProperty('preferred_username');
  }

  getUserProfile(): any {
    return this.keycloak.profile
  }

  getProperty(prop: string) {
    const tokenParsed = this.keycloak.tokenParsed as Record<string, unknown> | undefined;
    return tokenParsed?.[prop] ?? undefined;
  }

  hasRealmManagementRoles(): boolean {
    const tokenParsed = this.keycloak.tokenParsed as
      | (Record<string, unknown> & {
          resource_access?: Record<string, { roles?: string[] }>;
        })
      | undefined;
    if (tokenParsed && tokenParsed.resource_access && tokenParsed.resource_access['realm-management']) {
      const roles = tokenParsed.resource_access['realm-management'].roles;
      return roles && roles.length > 0;
    }
    return false;
  }

  async isLoggedIn() {
    let auth = !!this.keycloak.authenticated;
    this.isUserLogged.next(auth);
    return auth;
  }

  logout(): void {
    this.keycloak.logout({ redirectUri: this.environmentService.environment.keycloak.postLogoutRedirectUri });
    this.isUserLogged.next(false);
    this.cachedUserId = null;
    this.userIdLoaded$.next(null);
    sessionStorage.removeItem(this.USER_INFO_KEY);
  }

  private getUserInfo() {
    if (this.isLoadingUser) {
      return;
    }

    const username = this.getProperty('preferred_username');
    if (!username) {
      return;
    }

    this.isLoadingUser = true;

    this.apiService.userInfo().subscribe({
      next: response => {
        const user = response;
        if (user) {
          this.cachedUserId = user.__id ?? null;
          delete user.pwdhash;
          sessionStorage.setItem(this.USER_INFO_KEY, JSON.stringify(user));
          this.userIdLoaded$.next(this.cachedUserId);
        }
      },
      error: error => {
        console.error('Errore nel recupero delle informazioni utente', error);
        this.cachedUserId = null;
        sessionStorage.removeItem(this.USER_INFO_KEY);
      },
      complete: () => {
        this.isLoadingUser = false;
      }
    });
  }
}
